import { API_HEADERS } from "@sagot/constants";
import { unwrap } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { toast } from "@sagot/ui";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useErrorMessage } from "../../../lib/api-errors";
import { useApi } from "../../../session/session-provider";
import { isFacebookUrl } from "../channels/messenger-dialogs";
import { savePendingConnect, type PendingMetaConnect } from "./pending-connect";

/** Same-tab redirect (rule 8: no auth popups). Injected for tests. */
export type Redirect = (url: string) => void;

export function assignLocation(url: string): void {
  window.location.assign(url);
}

/**
 * Restart the Facebook sign-in from the callback page ("Choose Pages again" / "Grant permissions").
 * The backend always asks Facebook to re-show declined permissions and the Page picker.
 */
export function useRestartConnect(pending: PendingMetaConnect, redirect: Redirect = assignLocation) {
  const { t } = useTranslation();
  const api = useApi();
  const message = useErrorMessage();
  const [redirecting, setRedirecting] = useState(false);
  const start = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST("/v1/agents/{agent_id}/channels/messenger/connect", {
          params: { path: { agent_id: pending.agentId }, header: { [API_HEADERS.workspace]: pending.wsId } },
        }),
      ),
    onSuccess: ({ authorize_url }) => {
      if (!isFacebookUrl(authorize_url)) {
        toast.error(t("deployKnowledge.channels.errors.notConfigured"));
        return;
      }
      savePendingConnect({ wsSlug: pending.wsSlug, wsId: pending.wsId, agentId: pending.agentId });
      setRedirecting(true);
      redirect(authorize_url);
    },
    onError: (error) => toast.error(message(error)),
  });
  return { restart: () => { start.mutate(); }, busy: start.isPending || redirecting };
}
